import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FiCheck,
    FiShoppingBag,
    FiX,
    FiArrowRight,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useShop } from '../../context/ShopContext';
import { useDiscount } from '../../context/DiscountContext';
import { cashPriceFromMrp, getProductDiscount, formatPrice } from '../../utils/cashPricing';
import { BUSINESS_WHATSAPP_CHAT_URL } from '../../config/businessInfo';
import './AddToCartAnimationModal.css';

const AddToCartAnimationModal = () => {
    const { activeCartModal, closeCartModal, cartItemCount } = useShop();
    const { effectiveDiscount } = useDiscount();
    const navigate = useNavigate();
    const [isClosing, setIsClosing] = useState(false);
    const [animState, setAnimState] = useState('idle'); // 'idle' | 'running' | 'done'

    const handleManualClose = useCallback(() => {
        setIsClosing(true);
        setTimeout(() => {
            closeCartModal();
            setIsClosing(false);
            setAnimState('idle');
        }, 220);
    }, [closeCartModal]);

    useEffect(() => {
        if (!activeCartModal?.isOpen) {
            setIsClosing(false);
            setAnimState('idle');
            return undefined;
        }

        // Trigger the iconic cart animation right on open
        setAnimState('running');
        const timer = setTimeout(() => {
            setAnimState('done');
        }, 1600);

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                handleManualClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeCartModal, handleManualClose]);

    if (!activeCartModal?.isOpen) return null;

    const { product, quantity = 1 } = activeCartModal;
    const mrp = Number(product?.mrp || product?.price || 0);
    const discount = getProductDiscount(product, effectiveDiscount);
    const cashPrice = mrp > 0 ? cashPriceFromMrp(mrp, discount) : 0;
    const finalUnitPrice = cashPrice > 0 ? cashPrice : mrp;
    const totalPrice = finalUnitPrice * quantity;

    const handleViewCart = () => {
        closeCartModal();
        navigate('/cart');
    };

    const whatsappDirectLink = `${BUSINESS_WHATSAPP_CHAT_URL}&text=${encodeURIComponent(
        `Hello Rahber Corporation! I added "${product?.name || 'Appliance'}" (Qty: ${quantity}, Total: ${formatPrice(
            totalPrice
        )}) to my cart. Please confirm availability and Lahore delivery.`
    )}`;

    return (
        <div
            className={`add-to-cart-overlay ${isClosing ? 'is-closing' : ''}`}
            onClick={handleManualClose}
            role="dialog"
            aria-modal="true"
            aria-label="Item added to cart confirmation"
        >
            <div
                className={`add-to-cart-popup ${isClosing ? 'is-closing' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close Button */}
                <button
                    type="button"
                    className="add-to-cart-popup__close-btn"
                    onClick={handleManualClose}
                    aria-label="Close"
                    title="Close popup"
                >
                    <FiX />
                </button>

                {/* ── Authentic Codepen Animated Cart Stage ── */}
                <div className="add-to-cart-anim-stage">
                    {/* Sparkle Particles Burst */}
                    <div className="cart-particles-burst" aria-hidden="true">
                        <span className="cart-sparkle cs1">✦</span>
                        <span className="cart-sparkle cs2">★</span>
                        <span className="cart-sparkle cs3">✦</span>
                        <span className="cart-sparkle cs4">★</span>
                        <span className="cart-sparkle cs5">✦</span>
                        <span className="cart-sparkle cs6">★</span>
                    </div>

                    {/* The Morphing Button & Cart System from User's Snippet */}
                    <div className={`cart-interactive-box ${animState === 'running' || animState === 'done' ? 'active' : ''}`}>
                        <div className="cart-morph-btn">
                            <span className="cart-morph-text">
                                {animState === 'done' ? 'Added to Cart!' : 'Adding to Cart...'}
                            </span>
                            <svg className="cart-morph-curve" viewBox="0 0 64 13" aria-hidden="true">
                                <path d="M0 12C6 12 17 12 32 12C47.9024 12 58 12 64 12V13H0V12Z" />
                            </svg>

                            {/* Dropping Appliance / Item SVG */}
                            <div className="cart-dropping-shirt" aria-hidden="true">
                                <svg className="shirt-svg first" viewBox="0 0 24 24">
                                    <path d="M4.99997 3L8.99997 1.5C8.99997 1.5 10.6901 3 12 3C13.3098 3 15 1.5 15 1.5L19 3L22.5 8L19.5 10.5L19 9.5L17.1781 18.6093C17.062 19.1901 16.778 19.7249 16.3351 20.1181C15.4265 20.925 13.7133 22.3147 12 23C10.2868 22.3147 8.57355 20.925 7.66487 20.1181C7.22198 19.7249 6.93798 19.1901 6.82183 18.6093L4.99997 9.5L4.5 10.5L1.5 8L4.99997 3Z" fill="#EA580C" stroke="#FED7AA" strokeWidth="1" />
                                </svg>
                                <svg className="shirt-svg second" viewBox="0 0 24 24">
                                    <path d="M4.99997 3L8.99997 1.5C8.99997 1.5 10.6901 3 12 3C13.3098 3 15 1.5 15 1.5L19 3L22.5 8L19.5 10.5L19 9.5L17.1781 18.6093C17.062 19.1901 16.778 19.7249 16.3351 20.1181C15.4265 20.925 13.7133 22.3147 12 23C10.2868 22.3147 8.57355 20.925 7.66487 20.1181C7.22198 19.7249 6.93798 19.1901 6.82183 18.6093L4.99997 9.5L4.5 10.5L1.5 8L4.99997 3Z" fill="#FB923C" />
                                </svg>
                            </div>

                            {/* Rolling Cart with Wheels and Tick Checkmark */}
                            <div className="cart-rolling-icon" aria-hidden="true">
                                <svg viewBox="0 0 36 26" className="cart-rolling-svg">
                                    <path d="M1 2.5H6L10 18.5H25.5L28.5 7.5L7.5 7.5" className="cart-shape" />
                                    <path d="M11.5 25C12.6046 25 13.5 24.1046 13.5 23C13.5 21.8954 12.6046 21 11.5 21C10.3954 21 9.5 21.8954 9.5 23C9.5 24.1046 10.3954 25 11.5 25Z" className="cart-wheel" />
                                    <path d="M24 25C25.1046 25 26 24.1046 26 23C26 21.8954 25.1046 21 24 21C22.8954 21 22 21.8954 22 23C22 24.1046 22.8954 25 24 25Z" className="cart-wheel" />
                                    <path d="M14.5 13.5L16.5 15.5L21.5 10.5" className="cart-tick" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Pop-in Status Callout */}
                    <div className="cart-success-callout">
                        <span className="cart-success-badge-icon">
                            <FiCheck />
                        </span>
                        <div className="cart-success-badge-info">
                            <strong className="cart-success-title">Item Added to Your Cart!</strong>
                            <span className="cart-success-sub">Ready for checkout or continue exploring Lahore showroom</span>
                        </div>
                    </div>
                </div>

                {/* Product Snapshot Card */}
                <div className="add-to-cart-popup__product-row">
                    <img
                        src={
                            product?.image ||
                            'https://res.cloudinary.com/fl3tts9v/image/upload/v1790983151/category-air-conditioners_xtx2ok.jpg'
                        }
                        alt={product?.name || 'Appliance'}
                        className="add-to-cart-popup__thumb"
                    />
                    <div className="add-to-cart-popup__details">
                        <span className="add-to-cart-popup__brand">
                            {product?.brand || 'Rahber Corporation'}
                        </span>
                        <h4 className="add-to-cart-popup__name">
                            {product?.name || 'Genuine Home Appliance'}
                        </h4>
                        <div className="add-to-cart-popup__price-row">
                            <span className="add-to-cart-popup__cash-price">
                                {formatPrice(totalPrice)}
                            </span>
                            {quantity > 1 && (
                                <span className="add-to-cart-popup__unit-breakdown">
                                    ({formatPrice(finalUnitPrice)} × {quantity})
                                </span>
                            )}
                            {discount > 0 && cashPrice > 0 && cashPrice < mrp && (
                                <span className="add-to-cart-popup__cash-tag">
                                    Instant {discount}% Cash Discount
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Primary Dual Actions: View Full Cart & Continue Shopping */}
                <div className="add-to-cart-popup__actions-grid">
                    <button
                        type="button"
                        className="add-to-cart-popup__btn-viewcart"
                        onClick={handleViewCart}
                    >
                        <FiShoppingBag /> View Full Cart ({cartItemCount}) <FiArrowRight />
                    </button>
                    <button
                        type="button"
                        className="add-to-cart-popup__btn-continue"
                        onClick={handleManualClose}
                    >
                        Continue Shopping
                    </button>
                </div>

                {/* Direct WhatsApp Consultation Option */}
                <div className="add-to-cart-popup__whatsapp-wrap">
                    <a
                        href={whatsappDirectLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="add-to-cart-popup__btn-whatsapp"
                    >
                        <FaWhatsapp /> Ask Questions on WhatsApp (0327-8859655)
                    </a>
                </div>
            </div>
        </div>
    );
};

export default AddToCartAnimationModal;
